import React, { useEffect, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import "./App.css";

const qrPresets = {
  square: { dotsOptions: { color: "#000000", type: "square" }, cornersSquareOptions: { type: "square" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  rounded: { dotsOptions: { color: "#000000", type: "rounded" }, cornersSquareOptions: { type: "extra-rounded" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  dots: { dotsOptions: { color: "#000000", type: "dots" }, cornersSquareOptions: { type: "square" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  extraRounded: { dotsOptions: { color: "#000000", type: "extra-rounded" }, cornersSquareOptions: { type: "extra-rounded" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  classy: { dotsOptions: { color: "#000000", type: "classy" }, cornersSquareOptions: { type: "classy-rounded" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  futuristic: { dotsOptions: { color: "#000000", type: "extra-rounded" }, cornersSquareOptions: { type: "dot" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  sharp: { dotsOptions: { color: "#000000", type: "square" }, cornersSquareOptions: { type: "extra-rounded" }, cornersDotOptions: { type: "square" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  minimal: { dotsOptions: { color: "#000000", type: "dots" }, cornersSquareOptions: { type: "dot" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  edgy: { dotsOptions: { color: "#000000", type: "extra-rounded" }, cornersSquareOptions: { type: "classy-rounded" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  classicDots: { dotsOptions: { color: "#000000", type: "dots" }, cornersSquareOptions: { type: "square" }, cornersDotOptions: { type: "extra-rounded" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  retro: { dotsOptions: { color: "#000000", type: "classy" }, cornersSquareOptions: { type: "dot" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  clean: { dotsOptions: { color: "#000000", type: "square" }, cornersSquareOptions: { type: "extra-rounded" }, cornersDotOptions: { type: "extra-rounded" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },

  // Additional patterns
  futuristicDots: { dotsOptions: { color: "#000000", type: "dots" }, cornersSquareOptions: { type: "extra-rounded" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  circle: { dotsOptions: { color: "#000000", type: "circle" }, cornersSquareOptions: { type: "extra-rounded" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  star: { dotsOptions: { color: "#000000", type: "classy" }, cornersSquareOptions: { type: "dot" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  bubble: { dotsOptions: { color: "#000000", type: "extra-rounded" }, cornersSquareOptions: { type: "dot" }, cornersDotOptions: { type: "classy-rounded" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
  diamond: { dotsOptions: { color: "#000000", type: "classy" }, cornersSquareOptions: { type: "extra-rounded" }, cornersDotOptions: { type: "dot" }, backgroundOptions: { color: "#ffffff" }, qrOptions: { errorCorrectionLevel: "L" } },
};

function App() {
  const [text, setText] = useState("");
  const [styleKey, setStyleKey] = useState("square");
  const [size, setSize] = useState(250);
  const qrRef = useRef(null);
  const qrCode = useRef(null);

  // Initialize QR code once
  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      ...qrPresets[styleKey],
      width: size,
      height: size,
      data: text || "https://example.com",
      image: "",
    });

    qrCode.current.append(qrRef.current);
  }, []);

  // Update data
  useEffect(() => {
    if (qrCode.current) {
      qrCode.current.update({ data: text || "https://example.com" });
    }
  }, [text]);

  // Update style and size
  useEffect(() => {
    if (qrCode.current) {
      qrCode.current.update({ ...qrPresets[styleKey], width: size, height: size });
    }
  }, [styleKey, size]);

  const copyQRImage = async () => {
    if (!qrRef.current) return;
    const canvas = qrRef.current.querySelector("canvas");
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      try {
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        alert("QR image copied to clipboard!");
      } catch (err) {
        console.error(err);
        alert("Failed to copy QR image.");
      }
    });
  };

  return (
    <div className="app-wrapper">
      <div className="card">
        <div className="left-section">
          <h1 className="title">Black & White QR Generator</h1>
          <textarea
            className="input-box"
            placeholder="Enter text or link"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="size-selector">
            <label>
              QR Size:
              <select value={size} onChange={(e) => setSize(Number(e.target.value))}>
                <option value={250}>250</option>
                <option value={350}>350</option>
                <option value={450}>450</option>
                <option value={550}>550</option>
              </select>
            </label>
          </div>
        </div>

        <div className="qr-wrapper">
          <div className="qr-container" ref={qrRef}></div>
          <div className="style-buttons">
            {Object.keys(qrPresets).map((key) => (
              <button
                key={key}
                onClick={() => setStyleKey(key)}
                className={styleKey === key ? "active" : ""}
              >
                {key.toUpperCase()}
              </button>
            ))}
            <button className="copy-btn" onClick={copyQRImage}>
              COPY QR IMAGE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;